import React, { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid2 from "@mui/material/Grid2";
import CircularProgress from "@mui/material/CircularProgress";
import { getfirmwareInfo, resetFirmwareInfo } from "../../../action/report";
import { setAlert } from "../../../action/alert";
import customConfig from "../../../config";
import { selectMenuProps } from "../../../utils/selectMenuProps";
import FormInput from "../../Common/FormInputs/MuiTextField";
import AppButton from "../../Common/Button/Button";
import { useDesignTokens } from "../../../theme/useDesignTokens";
import Chart from "react-apexcharts";
import DataTable from "../../Common/DataTable/DataTable";

const FirmwareDistribution = ({
    allowedDeviceCategories,
    allowedCompanyBrand,
    report: { firmwareInfo },
    getfirmwareInfo,
    resetFirmwareInfo,
    setAlert
}) => {
    const [categoryFilter, setCategoryFilter] = useState("");
    const [brandFilter, setBrandFilter] = useState("");
    const [chipTypeFilter, setChipTypeFilter] = useState("")
    const [isFirmwareDataLoading, setIsFirmwareDataLoading] = useState(false);
    const [updateFlag, setUpdateFlag] = useState(undefined);
    const [showFirmwareData, setShowFirmwareData] = useState(false)
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [isFilterDisabled, setIsFilterDisabled] = useState(false);

    useEffect(() => {
        resetFirmwareInfo()
    }, [resetFirmwareInfo])


    useEffect(() => {
        if (!firmwareInfo || (firmwareInfo && Object.keys(firmwareInfo).length===0)) {
            setIsFirmwareDataLoading(false)
            setShowFirmwareData(false)
        }
        else {
            setIsFirmwareDataLoading(false)
            setShowFirmwareData(true)
        }
        setIsFilterDisabled(false)
    }, [firmwareInfo])

    useEffect(() => {
        setChipTypeFilter("")
        setShowFirmwareData(false)
        setIsFirmwareDataLoading(false)

    }, [categoryFilter, brandFilter])

    useEffect(() => {
        setShowFirmwareData(false)
        setIsFirmwareDataLoading(false)
    }, [chipTypeFilter])

    const handleSort = (key) => {
      let direction = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
      }
      setSortConfig({ key, direction });
    };


    const { colors } = useDesignTokens();

    const tableColumns = [
        { key: "srNo", label: "Sr", minWidth: 60, align: "left" },
        {
            key: "firmwareVersion",
            label: "Firmware Version",
            minWidth: 140,
            sortable: true,
        },
        {
            key: "count",
            label: "Device Count",
            minWidth: 140,
            sortable: true,
        },
        {
            key: "onboardedCount",
            label: "Onboarded Device Count",
            minWidth: 180,
            sortable: true,
        },
        {
            key: "percentage",
            label: "Percentage",
            minWidth: 140,
            sortable: true,
        },
    ];

    const buildTableData = () => {
        if (!firmwareInfo || !firmwareInfo.hasOwnProperty("firmwareDistribution")) {
            return [];
        }

        let sortedData = [...firmwareInfo.firmwareDistribution];

        if (sortConfig.key) {
            sortedData.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (typeof aValue === "string") {
                    return sortConfig.direction === "asc"
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                if (typeof aValue === "number") {
                    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
                }

                return 0;
            });
        }

        return sortedData.map((element, index) => {
            const { firmwareVersion, count, percentage, onboardedCount } = element;
            return {
                srNo: index + 1,
                firmwareVersion,
                count,
                onboardedCount,
                percentage,
                // use unique key field for DataTable
                rowId: `${categoryFilter.toLowerCase()}-${firmwareVersion}`,
            };
        });
    };

    const renderSummary = () => (
        <Paper
            sx={{
                pl: 2,
                pr: { xs: 3, md: 2 },
                py: 1.5,
                boxShadow: 2,
                borderRadius: 1,
                boxSizing: "border-box",
                width: { xs: "100%", md: "fit-content" },
                maxWidth: { xs: "100%", md: "none" },
                minWidth: { xs: 0, md: 400 },
                ml: { xs: 0, md: "auto" },
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    display: "grid",
                    gap: 0.75,
                }}
            >
                {[
                    { label: "Total Devices", value: firmwareInfo?.totalDevices ?? 0 },
                    { label: "Total Onboarded Devices", value: firmwareInfo?.totalOnboardedDevices ?? 0 },
                    { label: "Total Firmware Versions", value: firmwareInfo?.totalFirmwareVersions ?? 0 },
                ].map((item) => (
                    <Box
                        key={item.label}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1fr) auto",
                            alignItems: "center",
                            gap: 1.5,
                            py: 0.25,
                            minWidth: 0,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                lineHeight: 1.4,
                                fontSize: { xs: "0.9rem", md: "0.95rem" },
                                minWidth: 0,
                            }}
                        >
                            {item.label}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                minWidth: 32,
                                textAlign: "right",
                                color: "text.primary",
                                lineHeight: 1.4,
                                fontSize: { xs: "0.9rem", md: "0.95rem" },
                            }}
                        >
                            {item.value}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Paper>
    );

    function displayPieChart() {

        let firmwareVersions = [], firmwareCount = []

        if (firmwareInfo && firmwareInfo.hasOwnProperty('firmwareDistribution')) {

            firmwareInfo['firmwareDistribution'].map((element, index) => {
                const { firmwareVersion, count } = element;
                firmwareCount.push(count)
                firmwareVersions.push(firmwareVersion)
            });

            var options = {
                chart: {
                    width: 380,
                    type: 'pie',
                },
                labels: firmwareVersions,
                legend: {
                    position: 'bottom',
                    fontSize: '14px',
                    markers: { width: 10, height: 10 },
                },
                responsive: [
                    {
                        breakpoint: 768,
                        options: {
                            chart: { width: '100%' },
                            legend: {
                                position: 'bottom',
                                fontSize: '13px',
                                horizontalAlign: 'center',
                                markers: { width: 12, height: 12 },
                            },
                        },
                    },
                    {
                        breakpoint: 480,
                        options: {
                            chart: { width: '100%' },
                            legend: {
                                position: 'bottom',
                                fontSize: '12px',
                                horizontalAlign: 'center',
                                markers: { width: 10, height: 10 },
                                offsetY: 8,
                            },
                        },
                    },
                ],
            };

            return (
                <Box className="firmware-distribution-chart-wrap" sx={{ width: "100%" }}>
                    <Chart options={options} series={firmwareCount} type="pie" width="100%" />
                </Box>
            );
        }
    }

    function getFirmwareData() {
        setShowFirmwareData(false)

        if (categoryFilter.length===0) {
            setAlert("Category Cannot be Empty", 'danger')
            return
        }

        if (!brandFilter || brandFilter.length===0) {
            setAlert("Brand Cannot be Empty", 'danger')
            return
        }

        let chipTypeParam = ""
        if (!chipTypeFilter || chipTypeFilter.length===0) {
            chipTypeParam = ""
        }
        else {
            chipTypeParam = chipTypeFilter
        }

        setIsFirmwareDataLoading(true)
        setIsFilterDisabled(true)
        getfirmwareInfo(categoryFilter, brandFilter, chipTypeParam)
        setUpdateFlag(Math.random())
    }

    function resetFilters() {
        setCategoryFilter("")
        setBrandFilter("")
        setChipTypeFilter("")
        setUpdateFlag(Math.random())
        setIsFirmwareDataLoading(false)
        setShowFirmwareData(false)
    };

    const chipTypeOptions =
        categoryFilter === customConfig.deviceCategoryODMFRONTLOADWM
            ? (customConfig.chipTypesByCategory.FLWM || [])
            : categoryFilter === customConfig.deviceCategoryLED
                ? (customConfig.chipTypesByCategory.LED || [])
                : (customConfig.matterChipType || []);

    return (
        <Fragment>
            <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, mb: 2 }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr auto" },
                        gap: 2.5,
                        alignItems: "center",
                        maxWidth: "100%",
                    }}
                >
                    <FormInput
                        type="select"
                        label="Category"
                        size="small"
                        fullWidth
                        name="categoryFilter"
                        value={categoryFilter}
                        onChange={(e) => { setCategoryFilter(e.target.value); setChipTypeFilter(""); }}
                        options={(allowedDeviceCategories || []).map((el) => ({ value: el, label: el }))}
                        MenuProps={selectMenuProps}
                        sx={{ minWidth: 0 }}
                    />
                    <FormInput
                        type="select"
                        label="Brand"
                        size="small"
                        fullWidth
                        name="brandFilter"
                        value={brandFilter}
                        onChange={(e) => setBrandFilter(e.target.value)}
                        options={(allowedCompanyBrand || []).map((el) => ({ value: el, label: el }))}
                        MenuProps={selectMenuProps}
                        sx={{ minWidth: 0 }}
                    />
                    <FormInput
                        type="select"
                        label="Chip Type"
                        size="small"
                        fullWidth
                        name="chipTypeFilter"
                        value={chipTypeFilter}
                        onChange={(e) => setChipTypeFilter(e.target.value)}
                        options={chipTypeOptions.map((el) => ({ value: el, label: el }))}
                        MenuProps={selectMenuProps}
                        sx={{ minWidth: 0 }}
                    />
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: { xs: "flex-start", lg: "flex-end" } }}>
                        <AppButton variant="primary" size="medium" onClick={getFirmwareData} disabled={isFilterDisabled}>Filter</AppButton>
                        <AppButton variant="ghost" size="medium" onClick={resetFilters}>Reset</AppButton>
                    </Box>
                </Box>
            </Paper>
            {isFirmwareDataLoading ? (
                <Box sx={{ display: "grid", placeItems: "center", height: "20vh" }}>
                    <CircularProgress size={48} sx={{ color: colors?.primary?.main || "#AFB42B" }} />
                </Box>
            ) : (
                showFirmwareData && firmwareInfo && Object.keys(firmwareInfo).length > 0 && firmwareInfo.hasOwnProperty("firmwareDistribution") && (
                    <Fragment>
                        <Grid2 container spacing={2} sx={{ mb: 2 }}>
                            <Grid2 size={{ xs: 12, md: 6 }}>{displayPieChart()}</Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }}>{renderSummary()}</Grid2>
                        </Grid2>
                        <Paper sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
                            <DataTable
                                columns={tableColumns}
                                data={buildTableData()}
                                rowKey="rowId"
                                minWidth="100%"
                                maxHeight={{ xs: "none", md: "58vh" }}
                                stickyHeader
                                striped={false}
                                hoverEffect={false}
                                onSort={handleSort}
                                orderBy={sortConfig.key}
                                order={sortConfig.direction}
                                tableContainerSx={{
                                    boxShadow: "none",
                                    borderRadius: 0,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    width: { xs: "max-content", md: "100%" },
                                    minWidth: { xs: 660, md: "100%" },
                                    overflowX: { xs: "auto", md: "hidden" },
                                }}
                                sx={{ overflowX: { xs: "auto", md: "hidden" } }}
                                msgForDisplay="No Data found"
                            />
                        </Paper>
                    </Fragment>
                )
            )}
        </Fragment>
    );
}

const mapStateToProps = (state) => ({
    allowedDeviceCategories: state.auth.allowedDeviceCategories,
    allowedCompanyBrand: state.auth.allowCompanyBrand,
    report: state.report,
});

const mapDispatchToProps = (dispatch) => ({
    setAlert: (message, status) => dispatch(setAlert(message, status)),
    getfirmwareInfo: (categoryFilter, brandFilter, chipTypeFilter) => dispatch(getfirmwareInfo(categoryFilter, brandFilter, chipTypeFilter)),
    resetFirmwareInfo: () => dispatch(resetFirmwareInfo()),
});

export default connect(mapStateToProps, mapDispatchToProps)(FirmwareDistribution);